import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  ErrorHandler,
  HostBinding,
  Input,
  ViewChild,
} from '@angular/core';
import { Renderable } from '@technocom/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { SimpleAttachmentLinkService } from '@technocom/data';
import { FileActivateEvent, FileBoxErrorEvent, FileBoxSelectEvent, FileLinkManagerService } from '@technocom/ui';
import { Nil } from '@technocom/common/platform';
import { FileInfo } from '@angular-devkit/build-angular/src/utils/index-file/augment-index-html';
import { VideoFile } from '@app/shared/components/video-viewer/video-file';
import { HrefUtils } from '@technocom/common/http';

@Component({
  selector: 'app-video-viewer',
  templateUrl: './video-viewer.component.html',
  styleUrls: ['./video-viewer.component.scss'],
  viewProviders: [
    {
      provide: FileLinkManagerService,
      useExisting: SimpleAttachmentLinkService,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoViewerComponent extends Renderable {
  @HostBinding('class.ratio')
  hostBinding1 = true;
  @HostBinding('class.ratio-21x9')
  hostBinding2 = true;

  @Input() videos: Array<VideoFile> = [];
  @Input() dataItem: { id: number | string };

  @ViewChild('video', { read: ElementRef }) player: ElementRef<HTMLVideoElement>;

  active: VideoFile;

  constructor(
    private sanitizer: DomSanitizer,
    protected errorHandler: ErrorHandler,
    private attachService: SimpleAttachmentLinkService,
  ) {
    super();
  }

  protected onInit() {
    super.onInit();

    if (this.videos?.length > 0) {
      this.selectFile(this.videos[0]);
    }
  }

  onVideoEnd(e: Event) {
    const index = this.videos?.indexOf(this.active);
    if (index !== this.videos?.length - 1) {
      this.selectFile(this.videos[index + 1]);
    }
  }

  onFileActivate(e: FileActivateEvent<FileInfo>): void {
    const [first] = e.files;
    this.selectFile(first as unknown as VideoFile);
  }

  selectFile(file: VideoFile | Nil): void {
    const activeIndex = this.videos.indexOf(this.active);
    const selectedIndex = this.videos.indexOf(file);
    if (activeIndex !== selectedIndex) {
      this.player?.nativeElement.pause();
      this.active = file as any;
      this.player?.nativeElement.load();
    }
  }

  onFileSelect(e: FileBoxSelectEvent<any>): void {
    this.active = e.files[0];
    e.files[0].selected = true;
  }

  isActive(file: VideoFile): boolean {
    return file.uid === this.active?.uid;
  }

  onFileActionError(e: FileBoxErrorEvent): void {
    this.errorHandler.handleError(e.response);
  }

  isNew(file: VideoFile): boolean {
    return !file.id;
  }

  createUrl(file: VideoFile): string | null {
    if (this.isNew(file)) {
      return null;
    }

    return this.attachService.create(file.url);
  }

  safeUrl(video: VideoFile): SafeResourceUrl {
    const link = this.attachService.create(
      HrefUtils.join('/public', 'information', this.dataItem.id, 'file', video?.id),
    );
    return this.sanitizer.bypassSecurityTrustResourceUrl(link);
  }
}
